/* eslint-disable @typescript-eslint/no-explicit-any,react-hooks/rules-of-hooks */
import { juvareColors, themeColors } from '@juvare/common-web-ui/JuvareTheme';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Alert, Box, Link, Table, TableBody, TableCell, TableRow, Tooltip, Typography, useTheme, } from '@mui/material';
import React from 'react';
import rehypeParse from 'rehype-parse';
import rehypeReact from 'rehype-react';
import rehypeSanitize from 'rehype-sanitize';
import { unified } from 'unified';
import { icons } from '../icons/icons';
import { HtmlOutputContext } from './HtmlOutputContext';
import { createCustomJsxRuntime } from './custom-jsx-runtime';
/**
 * Renders HTML content safely using a custom processor instead of dangerously setting innerHTML.
 * Also allows support for custom tags and icons via a context provider.
 *
 * @component
 *
 * @param {HtmlOutputProps} props - Props for HtmlOutput component.
 * @param {string} props.html - Raw HTML string to be rendered.
 * @param {Record<string, CustomTag>} [props.customTags] - Optional mapping of custom tag names to components and allowed attributes.
 * @param {any} [props.data] - Optional data context that can be used inside custom tag render logic.
 * @param {{ [iconName: string]: React.FC<SvgIconProps> }} [props.customIcons] - Optional map of custom icons to override or inject SVG icons.
 *
 * @returns {React.ReactElement} The rendered HTML output using a safe custom processor.
 */
export const HtmlOutput = (props) => {
    // this is a better alternative to
    // return <div dangerouslySetInnerHTML={{ __html: html }} />;
    return (React.createElement(HtmlOutputContext.Provider, { value: { data: props.data } }, useProcessor(props.html, props.customTags, props.customIcons)));
};
function customComponentReducer(customTags) {
    if (!customTags) {
        return {};
    }
    return Object.keys(customTags).reduce((acc, tagName) => {
        const customTag = customTags[tagName];
        acc[tagName] = customTag.element;
        return acc;
    }, {});
}
function customAttributesReducer(customTags) {
    if (!customTags) {
        return {};
    }
    return Object.keys(customTags).reduce((acc, tagName) => {
        const customTag = customTags[tagName];
        acc[tagName] = customTag.attributes;
        return acc;
    }, {});
}
function useProcessor(html, customTags, customIcons) {
    const customAttributes = Object.assign(Object.assign({}, attributes), customAttributesReducer(customTags));
    const customHtmlComponentMap = Object.assign(Object.assign({}, htmlComponentMap), customComponentReducer(customTags));
    const tagNames = Object.keys(customHtmlComponentMap);
    const allIcons = Object.assign(Object.assign({}, icons), customIcons);
    const { jsx, jsxs, Fragment } = createCustomJsxRuntime(allIcons); // 🔥 inject customIcons
    const rehypeReactOptions = {
        jsx,
        jsxs,
        Fragment,
        components: customHtmlComponentMap,
    };
    const file = unified()
        .use(rehypeParse, { fragment: true })
        .use(rehypeSanitize, { tagNames: tagNames, attributes: customAttributes })
        .use(rehypeReact, rehypeReactOptions)
        .processSync(html);
    return file.result;
}
function HtmlTypography(component, fixedVariant) {
    return (props) => {
        const classNames = useClassNames(props);
        const variant = useVariant(classNames, fixedVariant);
        const style = useStyle(classNames, props.style);
        return (React.createElement(Typography, { component: component, variant: variant, style: style, "data-html-output": 'true' }, props.children));
    };
}
function HtmlTag(TagName) {
    return (props) => {
        const classNames = useClassNames(props);
        const style = useStyle(classNames, props.style);
        if (TagName === 'div') {
            const alertClassName = classNames === null || classNames === void 0 ? void 0 : classNames.find((className) => className.startsWith('alert-'));
            if (alertClassName) {
                const severity = alertClassName.substring(6);
                if (severity === 'success' || severity === 'info' || severity === 'warning' || severity === 'error') {
                    return React.createElement(Alert, { severity: severity }, props.children);
                }
                else {
                    return (React.createElement(Tooltip, { title: 'Unknown alert severity: ' + alertClassName },
                        React.createElement(Alert, { severity: 'error' }, props.children)));
                }
            }
        }
        else if (TagName === 'span') {
            const iconClassName = classNames === null || classNames === void 0 ? void 0 : classNames.find((className) => className.startsWith('icon-'));
            if (iconClassName) {
                const Icon = props.customIcons ? props.customIcons[iconClassName] : undefined;
                if (Icon) {
                    return React.createElement(Icon, { sx: { verticalAlign: 'middle' }, style: style });
                }
                else {
                    return (React.createElement(Tooltip, { title: 'Unknown icon: ' + iconClassName },
                        React.createElement(QuestionMarkIcon, { sx: { verticalAlign: 'middle' }, color: 'error', style: style })));
                }
            }
        }
        return (React.createElement(TagName, { style: style, "data-html-output": 'true' }, props.children));
    };
}
const HtmlLink = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(Link, { style: style, href: props.href, target: '_blank' }, props.children));
};
const HtmlDefinitionList = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(Box, { component: 'dl', sx: {
            marginBlockStart: 0,
            marginBlockEnd: 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            justifyContent: 'stretch',
            alignItems: 'stretch',
        }, style: style, "data-html-output": 'true' }, props.children));
};
const HtmlDefinitionTerm = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(Box, { component: 'dt', sx: {
            display: 'flex',
            alignItems: 'center',
            pt: 2,
            pb: 2,
            pr: 2,
            borderBottom: (theme) => '1px solid ' + theme.palette.divider,
            '&:first-of-type': { pt: 0 },
            '&:last-of-type': { pb: 0, borderBottom: 'none' },
        }, "data-html-output": 'true' },
        React.createElement(Typography, { variant: 'body2', style: style }, props.children)));
};
const HtmlDefinitionDefinition = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(Box, { component: 'dd', sx: {
            marginInlineStart: 0,
            pt: 2,
            pb: 2,
            borderBottom: (theme) => '1px solid ' + theme.palette.divider,
            '&:first-of-type': { pt: 0 },
            '&:last-of-type': { pb: 0, borderBottom: 'none' },
        }, "data-html-output": 'true' },
        React.createElement(Typography, { variant: 'subtitle2', style: style }, props.children)));
};
const HtmlImage = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return React.createElement(Box, { style: style, component: 'img', src: props.src, alt: props.alt, height: props.height, width: props.width });
};
const HtmlTable = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(Table, { style: style, "data-html-output": 'true' },
        React.createElement(TableBody, { "data-html-output": 'true' }, props.children)));
};
const HtmlTableRow = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(TableRow, { style: style, "data-html-output": 'true' }, props.children));
};
const HtmlTableCellHead = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(TableCell, { style: style, "data-html-output": 'true' }, props.children));
};
const HtmlTableCellData = (props) => {
    const classNames = useClassNames(props);
    const style = useStyle(classNames, props.style);
    return (React.createElement(TableCell, { style: style, "data-html-output": 'true' }, props.children));
};
function useClassNames(props) {
    if (props.className) {
        return props.className.split(/\s+/);
    }
    else {
        return undefined;
    }
}
function useVariant(classNames, variant) {
    if (variant) {
        return variant;
    }
    else if (classNames) {
        let foundVariant;
        for (const className of classNames) {
            if (variants.includes(className)) {
                foundVariant = className;
            }
        }
        return foundVariant;
    }
    else {
        return undefined;
    }
}
function useStyle(classNames, style) {
    const theme = useTheme();
    if (classNames) {
        let color;
        for (const className of classNames) {
            const f = colors[className];
            if (f) {
                color = f(theme);
            }
        }
        if (color) {
            return Object.assign({ color: color }, style);
        }
    }
    return style;
}
const variants = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'caption', 'button', 'overline'];
const colors = Object.assign(Object.assign({ primary: (theme) => theme.palette.primary.main, 'primary-light': (theme) => theme.palette.primary.light, 'primary-dark': (theme) => theme.palette.primary.dark, secondary: (theme) => theme.palette.secondary.main, 'secondary-light': (theme) => theme.palette.secondary.light, 'secondary-dark': (theme) => theme.palette.secondary.dark, error: (theme) => theme.palette.error.main, 'error-light': (theme) => theme.palette.error.light, 'error-dark': (theme) => theme.palette.error.dark, warning: (theme) => theme.palette.warning.main, 'warning-light': (theme) => theme.palette.warning.light, 'warning-dark': (theme) => theme.palette.warning.dark, info: (theme) => theme.palette.info.main, 'info-light': (theme) => theme.palette.info.light, 'info-dark': (theme) => theme.palette.info.dark, success: (theme) => theme.palette.success.main, 'success-light': (theme) => theme.palette.success.light, 'success-dark': (theme) => theme.palette.success.dark, 'grey-50': (theme) => theme.palette.grey[50], 'grey-100': (theme) => theme.palette.grey[100], 'grey-200': (theme) => theme.palette.grey[200], 'grey-300': (theme) => theme.palette.grey[300], 'grey-400': (theme) => theme.palette.grey[400], 'grey-500': (theme) => theme.palette.grey[500], 'grey-600': (theme) => theme.palette.grey[600], 'grey-700': (theme) => theme.palette.grey[700], 'grey-800': (theme) => theme.palette.grey[800], 'grey-900': (theme) => theme.palette.grey[900], 'grey-A100': (theme) => theme.palette.grey.A100, 'grey-A200': (theme) => theme.palette.grey.A200, 'grey-A400': (theme) => theme.palette.grey.A400, 'grey-A700': (theme) => theme.palette.grey.A700, 'text-primary': (theme) => theme.palette.text.primary, 'text-secondary': (theme) => theme.palette.text.secondary, 'text-disabled': (theme) => theme.palette.text.disabled, divider: (theme) => theme.palette.divider }, buildThemeColors()), buildJuvareColors());
function buildThemeColors() {
    const colors = {};
    walkThemeColors(themeColors, [], (colorNames, colorValue) => (colors['theme-' + colorNames.join('-')] = () => colorValue));
    return colors;
}
function walkThemeColors(colors, prefix, callback) {
    for (const [name, value] of Object.entries(colors)) {
        if (typeof value === 'string') {
            callback([...prefix, name], value);
        }
        else if (value != null) {
            walkThemeColors(value, [...prefix, name], callback);
        }
    }
}
function buildJuvareColors() {
    const colors = {};
    for (const [color, subColors] of Object.entries(juvareColors)) {
        for (const [subColor, value] of Object.entries(subColors)) {
            colors[`juvare-${color}-${subColor}`] = () => value;
        }
    }
    return colors;
}
const htmlComponentMap = {
    h1: HtmlTypography('h1', 'h1'),
    h2: HtmlTypography('h2', 'h2'),
    h3: HtmlTypography('h3', 'h3'),
    h4: HtmlTypography('h4', 'h4'),
    h5: HtmlTypography('h5', 'h5'),
    h6: HtmlTypography('h6', 'h6'),
    p: HtmlTypography('p'),
    div: HtmlTag('div'),
    img: HtmlImage,
    ul: HtmlTag('ul'),
    ol: HtmlTag('ol'),
    li: HtmlTag('li'),
    dl: HtmlDefinitionList,
    dt: HtmlDefinitionTerm,
    dd: HtmlDefinitionDefinition,
    table: HtmlTable,
    tr: HtmlTableRow,
    th: HtmlTableCellHead,
    td: HtmlTableCellData,
    pre: HtmlTag('pre'),
    span: HtmlTag('span'),
    strong: HtmlTag('strong'),
    em: HtmlTag('em'),
    b: HtmlTag('b'),
    i: HtmlTag('i'),
    a: HtmlLink,
    br: () => React.createElement("br", null),
};
const attributes = {
    '*': [],
    h1: ['className', 'style'],
    h2: ['className', 'style'],
    h3: ['className', 'style'],
    h4: ['className', 'style'],
    h5: ['className', 'style'],
    h6: ['className', 'style'],
    p: ['className', 'style'],
    div: ['className', 'style'],
    ul: ['className', 'style'],
    ol: ['className', 'style'],
    li: ['className', 'style'],
    dl: ['className', 'style'],
    dt: ['className', 'style'],
    dd: ['className', 'style'],
    table: ['className', 'style'],
    tr: ['className', 'style'],
    th: ['className', 'style'],
    td: ['className', 'style'],
    pre: ['className', 'style'],
    span: ['className', 'style'],
    strong: ['className', 'style'],
    em: ['className', 'style'],
    b: ['className', 'style'],
    i: ['className', 'style'],
    a: ['className', 'style', 'href'],
    img: ['className', 'style', 'src', 'alt', 'height', 'width'],
};
